import Money from "@dashboard/components/Money";
import Skeleton from "@dashboard/components/Skeleton";
import { orderUrl } from "@dashboard/orders/urls";
import { Box, List, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { OrderToday } from "../../utils";
import { homeSalesPanelMessages } from "./messages";

const MAX_ROWS = 8;
const MAX_LINE_THUMBNAILS = 3;

const OrderLinesPreview = ({ order }: { order: OrderToday }) => {
  const shown = order.lines.slice(0, MAX_LINE_THUMBNAILS);
  const extra = order.lines.length - shown.length;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {shown.map(line => (
        <Box
          key={line.id}
          as="img"
          src={line.thumbnail?.url}
          alt=""
          width={5}
          height={5}
          borderRadius={2}
          borderColor="default1"
          borderWidth={1}
          borderStyle="solid"
          objectFit="scale-down"
        />
      ))}
      <Text size="small" color="default2">
        {order.lines[0]?.productName}
        {extra > 0 && (
          <>
            {" "}
            <FormattedMessage
              {...homeSalesPanelMessages.extraLines}
              values={{ count: extra }}
            />
          </>
        )}
      </Text>
    </Box>
  );
};

interface HomeSalesPanelProps {
  orders: OrderToday[];
  loading: boolean;
  ordersListUrl: string;
  awaitingPaymentCount: number;
  unpaidOrdersUrl: string;
}

export const HomeSalesPanel = ({
  orders,
  loading,
  ordersListUrl,
  awaitingPaymentCount,
  unpaidOrdersUrl,
}: HomeSalesPanelProps) => {
  const intl = useIntl();

  // Lives in the panel, not on the card header: the header renders inside
  // Accordion.Trigger, which is a <button>, and a link nested in a button
  // would navigate and toggle the card at the same time.
  const awaitingPayment = awaitingPaymentCount > 0 && (
    <Box
      padding={3}
      borderColor="default1"
      borderWidth={1}
      borderTopStyle="solid"
    >
      <Link to={unpaidOrdersUrl}>
        <Text size="small" color="accent1">
          {intl.formatMessage(homeSalesPanelMessages.awaitingPayment, {
            count: awaitingPaymentCount,
          })}
        </Text>
      </Link>
    </Box>
  );

  if (loading) {
    return (
      <Box padding={3} display="flex" flexDirection="column" gap={2}>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (orders.length === 0) {
    // A day can have unpaid orders and still no contributing ones, so the
    // awaiting line has to survive the empty state.
    return (
      <Box display="flex" flexDirection="column">
        <Box padding={3}>
          <Text size="small" color="default2">
            {intl.formatMessage(homeSalesPanelMessages.noOrders)}
          </Text>
        </Box>
        {awaitingPayment}
      </Box>
    );
  }

  const shown = orders.slice(0, MAX_ROWS);
  const remaining = orders.length - shown.length;

  return (
    <Box display="flex" flexDirection="column">
      <List>
        {shown.map(order => (
          <List.Item
            key={order.id}
            borderColor="default1"
            borderWidth={1}
            borderTopStyle="solid"
            padding={3}
          >
            <Link to={orderUrl(order.id)}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                gap={3}
              >
                <Box display="flex" alignItems="center" gap={3}>
                  <Text size="small">#{order.number}</Text>
                  <Text size="small" color="default2">
                    {intl.formatTime(order.created)}
                  </Text>
                  <OrderLinesPreview order={order} />
                </Box>
                <Box display="flex" alignItems="center" gap={3}>
                  <Text size="small" color="default2">
                    {order.userEmail}
                  </Text>
                  <Text size="small">
                    <Money money={order.totalCharged} />
                  </Text>
                </Box>
              </Box>
            </Link>
          </List.Item>
        ))}
      </List>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={3}
        borderColor="default1"
        borderWidth={1}
        borderTopStyle="solid"
      >
        <Text size="small" color="default2">
          {remaining > 0 &&
            intl.formatMessage(homeSalesPanelMessages.moreOrders, {
              count: remaining,
            })}
        </Text>
        <Link to={ordersListUrl}>
          <Text size="small" color="accent1">
            {intl.formatMessage(homeSalesPanelMessages.viewAllOrders)}
          </Text>
        </Link>
      </Box>
      {awaitingPayment}
    </Box>
  );
};
